/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authClientsAttachToMcpServer } from "../funcs/authClientsAttachToMcpServer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AuthClient } from "../models/components/authclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AttachAuthClientToMcpServerRequest,
  AttachAuthClientToMcpServerSecurity,
} from "../models/operations/attachauthclienttomcpserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AttachAuthClientToMcpServerMutationVariables = {
  request: AttachAuthClientToMcpServerRequest;
  security?: AttachAuthClientToMcpServerSecurity | undefined;
  options?: RequestOptions;
};

export type AttachAuthClientToMcpServerMutationData = AuthClient;

export type AttachAuthClientToMcpServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * attachAuthClientToMcpServer authClients
 *
 * @remarks
 * Offer an existing auth client on another MCP server instead of creating a copy. The client stays one client: changing or rotating it changes upstream behavior on every server that offers it. Offering a client grants nobody permission to use it or access to the server. Takes effect only where typed auth client resolution is enforced. Requires org:admin.
 */
export function useAttachAuthClientToMcpServerMutation(
  options?: MutationHookOptions<
    AttachAuthClientToMcpServerMutationData,
    AttachAuthClientToMcpServerMutationError,
    AttachAuthClientToMcpServerMutationVariables
  >,
): UseMutationResult<
  AttachAuthClientToMcpServerMutationData,
  AttachAuthClientToMcpServerMutationError,
  AttachAuthClientToMcpServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAttachAuthClientToMcpServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyAttachAuthClientToMcpServer(): MutationKey {
  return ["@gram/client", "authClients", "attachToMcpServer"];
}

export function buildAttachAuthClientToMcpServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AttachAuthClientToMcpServerMutationVariables,
  ) => Promise<AttachAuthClientToMcpServerMutationData>;
} {
  return {
    mutationKey: mutationKeyAttachAuthClientToMcpServer(),
    mutationFn: function attachAuthClientToMcpServerMutationFn({
      request,
      security,
      options,
    }): Promise<AttachAuthClientToMcpServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authClientsAttachToMcpServer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
